const ACTIVE_EMPLOYEE_STATUS = 'active';
const EMAIL_ADDRESS_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

export interface SettingsEmployee {
  userUuid: string;
  email: string;
  name: string;
  imageUrl?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  status?: string;
}

/**
 * Reads a name out of a first.last style email, for people whose profile has no name: jane.doe@x.com -> Jane Doe.
 * Empty unless the part before the @ splits into at least two words, since a single word like "jsmith" can't be split.
 */
export function getNameFromEmail(email: string) {
  const [localPart = ''] = email.split('@');
  const words = localPart
    .replace(/\+.*$/, '')
    .split(/[._-]+/)
    .filter(Boolean);
  return words.length > 1 ? words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '';
}

/** A person's name, or one read from their email when their profile has none (the BFF then reports the email). */
export function getPersonName({
  name,
  firstName,
  lastName,
  email,
}: {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}) {
  const known = [name?.trim(), [firstName, lastName].filter(Boolean).join(' ')].find(
    (text) => text && text.toLowerCase() !== email.toLowerCase(),
  );
  return known || getNameFromEmail(email) || email;
}

export function getEmployeeDisplayName(employee: SettingsEmployee) {
  return getPersonName(employee);
}

export function getEmployeeInitials(employee: SettingsEmployee | undefined, fallback: string) {
  const name = employee ? getEmployeeDisplayName(employee) : fallback;
  return getNameInitials(name, fallback);
}

export function getNameInitials(name: string, fallback = '') {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');
  return (initials || fallback.slice(0, 2)).toUpperCase();
}

export function isActiveEmployee(employee: SettingsEmployee) {
  return !employee.status || employee.status === ACTIVE_EMPLOYEE_STATUS;
}

export function getEmailAddresses(value: string) {
  const emails = value.match(EMAIL_ADDRESS_PATTERN) ?? [];
  return [...new Set(emails.map((email) => email.toLowerCase()))];
}

export function getEmployeesByUserUuid(employees: SettingsEmployee[]) {
  const employeesByUserUuid = new Map<string, SettingsEmployee>();

  for (const employee of employees) {
    if (employee.userUuid) {
      employeesByUserUuid.set(employee.userUuid, employee);
    }
  }

  return employeesByUserUuid;
}
